import {
  EventMarker,
  Clip,
  SearchieTimeRangeCountsData,
  GenerateSearchieResponseProto,
  SearchieMatchingUsersData,
  SearchUser,
} from '@fullstory/proto/fs/services/alexa';
import { createSelector } from 'reselect';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MarkedPlayback, Playback, SessionExpander, SessionExpanderRoute } from '@fullstory/routes';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { AppState } from '../../types';
import { WelcomeState } from './types';
import { watchHistory } from '../watchHistory';
import { welcomeStateFromMatchingUsers, welcomeStateFromTimeRangeCounts } from './utils';
import { int64, Timestamp } from '@fullstory/proto/google/protobuf';

const sessions = (state: AppState) => state.matchingUsers.sessions;
const timeRange = (state: AppState) => state.matchingUsers.timeRange;
const allTimeRange = (state: AppState) => state.matchingUsers.allTimeRange;
const users = (state: AppState) => state.matchingUsers.users;
const cachedUsers = (state: AppState) => state.matchingUsers.cachedMatchingUsers;

const sessionsError = (state: AppState) => sessions(state).error;
const usersError = (state: AppState) => users(state).error;
const watchHistoryError = createSelector(watchHistory, history => history.error);

export const timeRangeError = (state: AppState) => timeRange(state).error;

export const sessionsData = (state: AppState) => sessions(state).data;
export const timeRangeData = (state: AppState) => timeRange(state).data;
export const allTimeRangeData = (state: AppState) => allTimeRange(state).data;
export const usersData = (state: AppState) => users(state).data;
export const cachedUsersData = (state: AppState) => cachedUsers(state).shownData;
export const cachedUsersStatus = (state: AppState) => cachedUsers(state).status;
export const watchHistoryData = createSelector(watchHistory, history => history.data);

export const isLoading = (state: AppState) => state.matchingUsers.isLoading;

export const hasError = createSelector(
  sessionsError,
  timeRangeError,
  usersError,
  watchHistoryError,
  (...args): boolean => args.some(s => s),
);

export const getTimeRangeSafeMessage = (state: AppState) => timeRange(state).safeMessage;

export const hasLoadedSuccessfully = createSelector(
  isLoading,
  hasError,
  sessionsData,
  timeRangeData,
  usersData,
  watchHistoryData,
  (isLoading, hasError, ...data): boolean => !isLoading && !hasError && data.every(s => s != null),
);

export const getUsers = createSelector(
  usersData,
  (users): SearchUser[] => users?.generatedSearchie?.matchingUsersData?.users ?? [],
);

export const getCachedUsers = createSelector(
  cachedUsersData,
  (users): SearchUser[] => users?.generatedSearchie?.matchingUsersData?.users ?? [],
);

export const getCachedNumMatchingUsers = createSelector(cachedUsersData, (users): number =>
  int64.toNumber(users?.generatedSearchie?.matchingUsersData?.matchingCounts?.numUsers),
);

export const getNumMatchingUsers = createSelector(usersData, (users): number =>
  int64.toNumber(users?.generatedSearchie?.matchingUsersData?.matchingCounts?.numUsers),
);

export const getNumMatchingSessions = createSelector(usersData, (users): number =>
  int64.toNumber(users?.generatedSearchie?.matchingUsersData?.matchingCounts?.numSessions),
);

export const getCachedMatchingSessions = createSelector(cachedUsersData, (users): number =>
  int64.toNumber(users?.generatedSearchie?.matchingUsersData?.matchingCounts?.numSessions),
);

export const getNumMatchingEvents = createSelector(usersData, (users): number =>
  int64.toNumber(users?.generatedSearchie?.matchingUsersData?.matchingCounts?.numEvents),
);

const getClip = (state: AppState, individualId: string): Clip | null => {
  const session = state.matchingUsers.sessions.data?.[individualId] ?? null;
  const clips = session?.clips?.clips ?? [];
  return clips[0] ?? null;
};

export const getEventMarkers = (state: AppState, user: SearchUser): EventMarker[] => {
  const clip = getClip(state, user.indvId as string);

  return clip?.eventMarkers ?? [];
};

export const getSessionLink = (
  user: SearchUser,
  eventMarkers: EventMarker[],
  segmentDefinition: SegmentDefinition,
  unnamedSegmentId: string,
): SessionExpanderRoute => {
  // If event markers were fetched in ClipContainer, use MarkedPlayback with those event markers
  if (eventMarkers && eventMarkers.length) {
    const hasOfflineActivity = eventMarkers.some(marker => marker.sessionId === '0');
    return hasOfflineActivity
      ? { route: [] } // cannot produce sessions for offline activity
      : { route: MarkedPlayback(user.indvId as string, eventMarkers) };
  }

  // Return an empty route for missing session since we cannot link to a valid session.
  if (user.lastMatchingUserSessionId === '') {
    return { route: [] };
  }

  // If we need to fetch session markers, use SessionExpander
  if (user.fetchSessionMarkers) {
    return SessionExpander(
      user.indvId as string,
      user.lastMatchingUserSessionId,
      segmentDefinition,
      unnamedSegmentId,
    );
  }

  // Default unmarked playback
  return {
    route: Playback(user.indvId as string, user.lastMatchingUserSessionId),
  };
};

// Extracts the welcome state from the all-time time range counts data.
export function welcomeStateFromAllTimeCounts(
  trcData: SearchieTimeRangeCountsData | null | undefined,
): WelcomeState {
  if (!int64.toNumber(trcData?.totalCounts?.numUsers)) {
    return WelcomeState.EMPTY_ORG;
  }
  return WelcomeState.HAS_SESSIONS;
}

// Extracts the count of matching users from matching users data.
export function numMatchingFromMatchingUsers(
  muData: SearchieMatchingUsersData | null,
): number | null {
  return muData?.matchingCounts ? int64.toNumber(muData?.matchingCounts?.numUsers) : null;
}

// Obtain segment bar counts from the matching users for the current segment.
// If complete is false, then the counts need to be obtained from the time range counts data.
export function segmentBarCountsFromMatchingUsers(muData: SearchieMatchingUsersData | null): {
  complete: boolean;
  counts: SegmentBarCounts;
} {
  let complete: boolean;
  let counts: SegmentBarCounts;

  // Negative num events indicates matching users couldn't gather session and event counts and that time range counts is required.
  if (muData?.matchingCounts && int64.toNumber(muData.matchingCounts.numEvents) >= 0) {
    complete = true;
    counts = {
      matchingUsers: int64.toNumber(muData.matchingCounts.numUsers),
      matchingSessions: int64.toNumber(muData.matchingCounts.numSessions),
      matchingEvents: int64.toNumber(muData.matchingCounts.numEvents),
      totalUsers: int64.toNumber(muData.matchingCounts.numUsers), // because segment is time-range only
      totalSessions: int64.toNumber(muData.matchingCounts.numSessions), // ...
      totalEvents: int64.toNumber(muData.matchingCounts.numEvents), // ...
    };
  } else {
    complete = false;
    counts = {
      matchingUsers: muData?.matchingCounts ? int64.toNumber(muData.matchingCounts.numUsers) : 0,
      matchingSessions: muData?.matchingCounts
        ? int64.toNumber(muData.matchingCounts.numSessions)
        : 0,
      matchingEvents: muData?.matchingCounts ? int64.toNumber(muData.matchingCounts.numEvents) : 0,
      totalUsers: -1,
      totalSessions: -1,
      totalEvents: -1,
    };
  }

  return { complete, counts };
}

export function matchingUserTimeRangeCounts(muData: GenerateSearchieResponseProto | null) {
  const data = muData?.generatedSearchie?.matchingUsersData ?? null;
  const welcomeState = welcomeStateFromMatchingUsers(data);
  const numMatching = numMatchingFromMatchingUsers(data);
  const { counts, complete: countsComplete } = segmentBarCountsFromMatchingUsers(data);
  return {
    welcomeState,
    numMatching,
    counts,
    countsComplete,
    countsComputedAt: muData?.computedAt ? Timestamp.toMillis(muData.computedAt) : -1,
    shouldGenerateTimeRangeCounts: welcomeState == null || numMatching == null || !countsComplete,
  };
}

// Extracts the count of matching users from time range counts data.
export function numMatchingFromTimeRangeCounts(
  muData: SearchieMatchingUsersData | null,
  trcData: SearchieTimeRangeCountsData | null,
): number {
  // first, try the matching users data, which we can use if available
  if (muData != null && int64.toNumber(muData?.matchingCounts?.numUsers) >= 0) {
    return int64.toNumber(muData?.matchingCounts?.numUsers);
  }
  if (!trcData?.matchingCounts) {
    return 0;
  }
  return int64.toNumber(trcData.matchingCounts.numUsers);
}

function zeroSegmentBarCounts(): SegmentBarCounts {
  return {
    matchingUsers: 0,
    matchingSessions: 0,
    matchingEvents: 0,
    totalUsers: 0,
    totalSessions: 0,
    totalEvents: 0,
  };
}

// If count is literally 0, return 0. If count is unset (null or undefined) return -1.
// Otherwise returns the count.
function optionalMatchingCount(count: int64 | undefined): number {
  if (count == undefined) {
    return -1;
  }
  return int64.toNumber(count);
}

// Obtain the segment bar counts from time range counts data.
export function segmentBarCountsFromTimeRangeCounts(
  trcData: SearchieTimeRangeCountsData | null,
): SegmentBarCounts {
  if (trcData == null) {
    return zeroSegmentBarCounts();
  }
  return {
    matchingUsers: optionalMatchingCount(trcData?.matchingCounts?.numUsers), // -1 will engage progressive rendering.
    matchingSessions: optionalMatchingCount(trcData?.matchingCounts?.numSessions), // ...
    matchingEvents: optionalMatchingCount(trcData?.matchingCounts?.numEvents), // ...
    totalUsers: int64.toNumber(trcData?.totalCounts?.numUsers),
    totalSessions: int64.toNumber(trcData?.totalCounts?.numSessions),
    totalEvents: int64.toNumber(trcData?.totalCounts?.numEvents),
  };
}
export type timeRangeCountsState = {
  welcomeState: WelcomeState | null;
  numMatching: number | null;
  counts: SegmentBarCounts;
  countsComputedAt: number;
};

export const makeTimeRangeCounts = (segmentDefinition: SegmentDefinition) => {
  const getter = makeTimeRangeCountsGetter(segmentDefinition);
  return createSelector(usersData, timeRangeData, allTimeRangeData, getter);
};

export const useTimeRangeCounts = (segmentDefinition: SegmentDefinition) => {
  const getTimeRangeCounts = useMemo(
    () => makeTimeRangeCounts(segmentDefinition),
    [segmentDefinition],
  );
  return useSelector(getTimeRangeCounts);
};

function makeTimeRangeCountsGetter(segmentDefinition: SegmentDefinition) {
  return (
    users: GenerateSearchieResponseProto | null,
    timeRange: GenerateSearchieResponseProto | null,
    allTimeRange: GenerateSearchieResponseProto | null,
  ): timeRangeCountsState => {
    const { shouldGenerateTimeRangeCounts, ...fromMatchingUsers } =
      matchingUserTimeRangeCounts(users);
    if (!shouldGenerateTimeRangeCounts) return fromMatchingUsers;
    const timeRangeData = timeRange?.generatedSearchie?.timeRangeCountsData ?? null;
    const userData = users?.generatedSearchie?.matchingUsersData ?? null;
    const timeRangeComputedAt = Timestamp.toMillis(timeRange?.computedAt) ?? -1;
    const fromTimeRange = {
      welcomeState:
        fromMatchingUsers.welcomeState === null
          ? welcomeStateFromTimeRangeCounts(timeRangeData, segmentDefinition)
          : fromMatchingUsers.welcomeState,
      numMatching:
        fromMatchingUsers.numMatching === null
          ? numMatchingFromTimeRangeCounts(userData, timeRangeData)
          : fromMatchingUsers.numMatching,
      counts: !fromMatchingUsers.countsComplete
        ? mergeSegmentCounts(
            fromMatchingUsers.counts,
            segmentBarCountsFromTimeRangeCounts(timeRangeData),
          )
        : fromMatchingUsers.counts,
      countsComputedAt: !fromMatchingUsers.countsComplete
        ? timeRangeComputedAt
        : fromMatchingUsers.countsComputedAt,
    };
    if (fromTimeRange.welcomeState !== null) return fromTimeRange;
    return {
      ...fromTimeRange,
      welcomeState: welcomeStateFromAllTimeCounts(
        allTimeRange?.generatedSearchie?.timeRangeCountsData ?? null,
      ),
    };
  };
}

interface SegmentBarCounts {
  matchingUsers: number;
  totalUsers: number;
  matchingSessions: number;
  totalSessions: number;
  matchingEvents: number;
  totalEvents: number;
}

// Will return a new SegmentBarCounts containing the existing counts replaced by new counts from c IFF they are >= 0;
function mergeSegmentCounts(existing: SegmentBarCounts, c: SegmentBarCounts): SegmentBarCounts {
  return {
    matchingUsers: c.matchingUsers >= 0 ? c.matchingUsers : existing.matchingUsers,
    totalUsers: c.totalUsers >= 0 ? c.totalUsers : existing.totalUsers,
    matchingSessions: c.matchingSessions >= 0 ? c.matchingSessions : existing.matchingSessions,
    totalSessions: c.totalSessions >= 0 ? c.totalSessions : existing.totalSessions,
    matchingEvents: c.matchingEvents >= 0 ? c.matchingEvents : existing.matchingEvents,
    totalEvents: c.totalEvents >= 0 ? c.totalEvents : existing.totalEvents,
  };
}
